import {BehaviorMetric, Metric} from 'app_api/projects/metrics/types';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {Translatable} from 'common/i18n';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import {getIconForSavedMetric} from 'common/report/insights/util/metrics';

import {FormulaMenuItemValue} from '../formulas-menu/types';
import {
  FormulaSectionItem,
  MenuType,
  MultiMetricsSection,
  SavedMetricMenuItemValue,
  SavedMetricSectionItem,
} from './types';

export function useSavedMetrics(excludedBehaviorTypes?: Set<MetricType>): BehaviorMetric[] {
  const metrics = useMetrics();
  let items = metrics
    .getAllValues()
    .filter((metric) => metric.type === TopLevelMetricType.Metric && metric.can_view) as BehaviorMetric[];

  /** filters saved metrics to be of a certain type */
  if (excludedBehaviorTypes?.size) {
    items = items.filter((item) => excludedBehaviorTypes.has(item.definition.behavior.type!) === false);
  }

  return items;
}

function getMetricMenuItem(metric: Metric): SavedMetricSectionItem | FormulaSectionItem {
  const icon = getIconForSavedMetric(metric);
  if (metric.type === `formula`) {
    return {
      icon,
      label: metric.name,
      menuType: MenuType.Formula,
      value: metric,
    };
  }
  return {
    icon,
    label: metric.name,
    menuType: MenuType.SavedMetric,
    value: metric,
  };
}

export function getMetricSection({
  label,
  metrics,
  isRecentSection = false,
}: {
  label: Translatable;
  metrics: Metric[];
  isRecentSection?: boolean;
}): MultiMetricsSection<SavedMetricMenuItemValue | FormulaMenuItemValue> {
  return {
    label,
    isRecentSection,
    items: metrics.map(getMetricMenuItem),
  };
}

export function getMetricsSections({
  recentMetricItems,
  savedMetrics,
  savedFormulas,
}: {
  recentMetricItems: Array<SavedMetricMenuItemValue | FormulaMenuItemValue>;
  savedMetrics: Metric[];
  savedFormulas: Metric[];
}): Array<MultiMetricsSection<Metric>> {
  const combinedMetrics = [...savedMetrics, ...savedFormulas];
  if (combinedMetrics.length) {
    const recentMetrics = combinedMetrics.filter((savedMetric) =>
      recentMetricItems.some((item) => item.id === savedMetric.id),
    );

    const showRecents = savedMetrics.length > 6 && recentMetrics.length > 0;
    return [
      /** only display a recently used section when there are enough items to fill in the dropdown */
      getMetricSection({
        label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
        metrics: showRecents ? recentMetrics : [],
        isRecentSection: true,
      }),
      getMetricSection({
        label: showRecents ? ({t}) => t(`query-builder.multi-metrics-menu.section-saved-metrics`) : ``,
        metrics: combinedMetrics,
      }),
    ].filter(Boolean);
  }
  return [];
}
